import { Fragment, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash/startCase';

import { TeamInput } from '__generated__/graphql';

import { SectionTitle } from '../../Common/Layout';
import { useEvent } from '../../Common/EventProvider';
import { useTracking } from '../../../common/Tracking';
import CheckoutContext from '../CheckoutContext';
import TeamPicker, { TeamMode } from '../../../common/Teams/TeamPicker';
import UI from '../../../common/UI';

const TeamForm = () => {
  const { t } = useTranslation();
  const { track } = useTracking();
  const { event } = useEvent();

  const {
    form,
    editTeam,
    teamFlags,
    validators,
    touched,
    touch,
    untouch,
  } = useContext(CheckoutContext);

  const teamModeRef = useRef<Record<string, TeamMode>>({});

  const handleChange = (teamTypeId: string, value: TeamInput | null, mode: TeamMode) => {
    editTeam(teamTypeId, () => value);

    if (mode !== teamModeRef.current[teamTypeId]) {
      track?.(`Checkout:TeamModeSelected:${startCase(mode)}`);
      teamModeRef.current[teamTypeId] = mode;
    }
  };

  const teamTypes = event.team_types.filter((teamType) => (
    teamFlags[teamType.id].allowCreateTeam || teamFlags[teamType.id].allowJoinTeam
  ));

  return (
    <UI.FadeIn>
      {teamTypes.map((teamType, index) => {
        const formIndex = form.teams.findIndex((value) => value.team_type.id === teamType.id);
        const teamErrors = validators.team.errors[`teams.${formIndex}`] ?? {};

        return (
          <Fragment key={teamType.id}>
            {index > 0 && <UI.HR />}

            <UI.FormGrid>
              <UI.Div>
                <SectionTitle sc={{ mb: 1 }}>
                  {teamType.title || t('team')}
                </SectionTitle>
                <UI.FlexContainer sc={{ gap: 0.25, wrap: 'wrap', small: true }}>
                  {teamFlags[teamType.id].allowedTickets.map((ticket) => (
                    <UI.Label key={ticket.id}>
                      <UI.Icon>
                        <UI.Icons.Ticket />
                      </UI.Icon>
                      {' '}
                      {ticket.title}
                    </UI.Label>
                  ))}
                </UI.FlexContainer>
              </UI.Div>
              <TeamPicker
                team={form.teams[formIndex]}
                eventId={event.id}
                teamType={teamType}
                allowIndividuals={teamFlags[teamType.id].allowIndividuals}
                allowCreateTeam={teamFlags[teamType.id].allowCreateTeam}
                allowJoinTeam={teamFlags[teamType.id].allowJoinTeam}
                onChange={(value, mode) => handleChange(teamType.id, value, mode)}
                touched={{
                  title: !!touched(`teams.${formIndex}.title`),
                  id: !!touched(`teams.${formIndex}.id`),
                  access_code: !!touched(`teams.${formIndex}.access_code`),
                }}
                touch={(field: string) => touch(`teams.${formIndex}.${field}`)}
                untouch={(field?: string) => (field ? untouch(`teams.${formIndex}.${field}`) : untouch(`teams.${formIndex}`))}
                errors={teamErrors}
              />
            </UI.FormGrid>
          </Fragment>
        );
      })}
    </UI.FadeIn>
  );
};

export default TeamForm;
